import React, { useMemo, useState } from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '@pagespeedify/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PerformanceTrendChartProps {
    reports: any[];
}

const MIN_DATA_POINTS = 2; // Minimum data points required for a meaningful trend

export default function PerformanceTrendChart({ reports }: PerformanceTrendChartProps) {
    const [currentUrlIndex, setCurrentUrlIndex] = useState(0);

    // Helper to get data for a URL within a date range
    const getUrlDataInRange = (url: string, daysAgo: number) => {
        const now = new Date();
        const cutoffDate = new Date();
        cutoffDate.setDate(now.getDate() - daysAgo);

        const dailyScores: Record<string, { total: number; count: number; date: Date }> = {};

        reports.forEach(report => {
            const reportUrl = report.website_url || report.page_url || report.url;
            if (reportUrl !== url) return;

            const dateStr = report.created_at || report.generatedAt || report.requestedAt;
            if (!dateStr) return;

            const date = new Date(dateStr);
            if (date < cutoffDate) return;

            const dateKey = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            const score = report.performanceScore ?? report.performance_score;

            if (typeof score === 'number') {
                if (!dailyScores[dateKey]) {
                    dailyScores[dateKey] = { total: 0, count: 0, date };
                }
                dailyScores[dateKey].total += score;
                dailyScores[dateKey].count += 1;
            }
        });

        const result = Object.entries(dailyScores)
            .map(([dateLabel, data]) => ({
                date: dateLabel,
                score: Math.round(data.total / data.count),
                rawDate: data.date
            }))
            .sort((a, b) => a.rawDate.getTime() - b.rawDate.getTime());

        return result;
    };

    // Get URLs that have enough data points (minimum 2) for a meaningful trend
    const urlsWithData = useMemo(() => {
        if (!reports || reports.length === 0) return [];

        // Get all unique URLs
        const allUrls = new Set<string>();
        reports.forEach(r => {
            const url = r.website_url || r.page_url || r.url;
            if (url) allUrls.add(url);
        });

        // Filter to only URLs that have at least MIN_DATA_POINTS in either 30 or 7 days
        const validUrls: { url: string; dataPoints: number; range: number }[] = [];

        allUrls.forEach(url => {
            const data30 = getUrlDataInRange(url, 30);
            const data7 = getUrlDataInRange(url, 7);

            // Check if 30-day data has enough points
            if (data30.length >= MIN_DATA_POINTS) {
                validUrls.push({ url, dataPoints: data30.length, range: 30 });
            }
            // Otherwise check 7-day data
            else if (data7.length >= MIN_DATA_POINTS) {
                validUrls.push({ url, dataPoints: data7.length, range: 7 });
            }
            // Skip URLs without enough data points
        });

        // Sort by most data points and take top 5
        return validUrls
            .sort((a, b) => b.dataPoints - a.dataPoints)
            .slice(0, 5)
            .map(v => ({ url: v.url, range: v.range }));
    }, [reports]);

    const selectedUrlData = urlsWithData[currentUrlIndex] || null;
    const selectedUrl = selectedUrlData?.url || null;
    const lookbackDays = selectedUrlData?.range || 30;

    // Get chart data for selected URL
    const chartData = useMemo(() => {
        if (!selectedUrl) return [];
        return getUrlDataInRange(selectedUrl, lookbackDays);
    }, [reports, selectedUrl, lookbackDays]);

    // Calculate actual date range from data
    const dateRangeLabel = useMemo(() => {
        if (chartData.length === 0) return '';
        if (chartData.length === 1) return chartData[0].date;

        const firstDate = chartData[0].date;
        const lastDate = chartData[chartData.length - 1].date;
        return `${firstDate} - ${lastDate}`;
    }, [chartData]);

    // Don't render anything if no URLs have enough data
    if (!reports || reports.length === 0 || urlsWithData.length === 0) return null;

    const latestScore = chartData.length > 0 ? chartData[chartData.length - 1].score : 0;

    let strokeColor = '#ef4444'; // red (Poor)
    let statusBorder = "bg-red-50 text-red-700 border-red-200";

    if (latestScore >= 90) {
        strokeColor = '#22c55e'; // green (Good)
        statusBorder = "bg-green-50 text-green-700 border-green-200";
    } else if (latestScore >= 50) {
        strokeColor = '#f97316'; // orange (Needs Improvement)
        statusBorder = "bg-orange-50 text-orange-700 border-orange-200";
    }

    const handlePrev = () => {
        setCurrentUrlIndex(prev => (prev > 0 ? prev - 1 : urlsWithData.length - 1));
    };

    const handleNext = () => {
        setCurrentUrlIndex(prev => (prev < urlsWithData.length - 1 ? prev + 1 : 0));
    };

    // Truncate URL for display
    const truncateUrl = (url: string, maxLen: number = 40) => {
        if (url.length <= maxLen) return url;
        return url.substring(0, maxLen) + '...';
    };

    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            const score = payload[0].value;
            let tooltipStatus = "Poor";
            let statusColor = "text-red-600";

            if (score >= 90) {
                tooltipStatus = "Good";
                statusColor = "text-green-600";
            } else if (score >= 50) {
                tooltipStatus = "Needs Improvement";
                statusColor = "text-orange-600";
            }

            return (
                <div className="bg-background border rounded-lg shadow-lg p-2 text-xs">
                    <p className="font-semibold mb-1">{label}</p>
                    <p className="flex items-center gap-2">
                        <span>Score: {score}</span>
                        <span className={`font-medium ${statusColor}`}>({tooltipStatus})</span>
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <Card className="w-full">
            <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                        <CardTitle className="text-base font-medium flex items-center gap-2">
                            Performance Trend
                            {chartData.length > 0 && (
                                <span className={`text-xs px-2 py-0.5 rounded-full border ${statusBorder}`}>
                                    Avg: {latestScore}
                                </span>
                            )}
                        </CardTitle>
                        <p className="text-xs text-muted-foreground mt-1">{dateRangeLabel}</p>
                    </div>

                    {/* Arrow Navigation */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePrev}
                            className="p-1 rounded hover:bg-muted transition-colors"
                            aria-label="Previous URL"
                        >
                            <ChevronLeft className="w-5 h-5" />
                        </button>
                        <span className="text-xs text-muted-foreground max-w-[200px] truncate" title={selectedUrl || ''}>
                            {truncateUrl(selectedUrl || '', 30)}
                        </span>
                        <span className="text-xs text-muted-foreground">
                            ({currentUrlIndex + 1}/{urlsWithData.length})
                        </span>
                        <button
                            onClick={handleNext}
                            className="p-1 rounded hover:bg-muted transition-colors"
                            aria-label="Next URL"
                        >
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </CardHeader>
            <CardContent>
                <div className="h-[200px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                                <linearGradient id="colorScore" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={strokeColor} stopOpacity={0.3} />
                                    <stop offset="95%" stopColor={strokeColor} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
                            <XAxis
                                dataKey="date"
                                tick={{ fontSize: 10 }}
                                tickLine={false}
                                axisLine={false}
                                minTickGap={30}
                            />
                            <YAxis
                                domain={[0, 100]}
                                tick={{ fontSize: 10 }}
                                tickLine={false}
                                axisLine={false}
                                ticks={[0, 50, 90, 100]}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Area
                                type="monotone"
                                dataKey="score"
                                stroke={strokeColor}
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorScore)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </CardContent>
        </Card>
    );
}
